import React, { useState, useRef, useEffect } from "react";
import { Line, Bar, Pie } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// ────────────────────────────────────────────────
// Chart Configurations (extracted for clarity)
const PATIENT_TREND_DATA = {
  labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  datasets: [
    {
      label: "Blood Sugar",
      data: [120, 130, 115, 140, 110, 105],
      borderColor: "#22c55e",
      backgroundColor: "#22c55e80", // slight transparency looks better on dark bg
    },
  ],
};

const DOCTOR_RECOVERY_DATA = {
  labels: ["Drug A", "Drug B", "Drug C"],
  datasets: [
    {
      label: "Recovery %",
      data: [78, 65, 82],
      backgroundColor: "#3b82f6",
    },
  ],
};

const PHARMA_EFFECTIVENESS_DATA = {
  labels: ["Effective", "Moderate", "Low"],
  datasets: [
    {
      data: [60, 25, 15],
      backgroundColor: ["#22c55e", "#f59e0b", "#ef4444"],
    },
  ],
};

const GOVT_DISEASE_DATA = {
  labels: ["Diabetes", "Hypertension", "Psoriasis"],
  datasets: [
    {
      label: "Cases",
      data: [5000, 4200, 900],
      backgroundColor: "#8b5cf6",
    },
  ],
};

// ────────────────────────────────────────────────
// Reusable Card Wrapper
const Card = ({ title, children, className = "" }) => (
  <div className={`bg-gray-900 p-4 rounded-xl ${className}`}>
    {title && <h2 className="font-semibold mb-3 text-base text-yellow-300">{title}</h2>}
    {children}
  </div>
);

// ────────────────────────────────────────────────
// Chart Components with proper destroy on unmount/tab change
const PatientTrendChart = () => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  if (!isMounted) return null;

  return (
    <Card title="Health Trend">
      <div ref={containerRef} className="h-64 w-full relative">
        <Line
          ref={chartRef}
          data={PATIENT_TREND_DATA}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { labels: { color: "#e5e7eb" } } },
            scales: {
              y: { ticks: { color: "#e5e7eb" } },
              x: { ticks: { color: "#e5e7eb" } },
            },
            // Safety: disable resize if container gone
            onResize: (chart) => {
              if (!containerRef.current?.ownerDocument) {
                return;
              }
            },
          }}
        />
      </div>
    </Card>
  );
};

const DoctorRecoveryChart = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, []);

  return (
    <Card title="Treatment Effectiveness">
      <div className="h-64">
        <Bar
          ref={chartRef}
          data={DOCTOR_RECOVERY_DATA}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { labels: { color: "#e5e7eb" } } },
            scales: { y: { ticks: { color: "#e5e7eb" } }, x: { ticks: { color: "#e5e7eb" } } },
          }}
        />
      </div>
    </Card>
  );
};

const PharmaEffectivenessChart = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, []);

  return (
    <Card title="Drug Effectiveness Analytics">
      <div className="h-64">
        <Pie
          ref={chartRef}
          data={PHARMA_EFFECTIVENESS_DATA}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom", labels: { color: "#e5e7eb" } },
            },
          }}
        />
      </div>
    </Card>
  );
};

const GovtDiseaseChart = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, []);

  return (
    <Card title="Public Health Dashboard">
      <div className="h-64">
        <Bar
          ref={chartRef}
          data={GOVT_DISEASE_DATA}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { labels: { color: "#e5e7eb" } } },
            scales: { y: { ticks: { color: "#e5e7eb" } }, x: { ticks: { color: "#e5e7eb" } } },
          }}
        />
      </div>
    </Card>
  );
};

// ────────────────────────────────────────────────
// Tab Content Components
const PatientTab = () => (
  <div className="space-y-4">
    <Card title="Digital Medicine Box">
      <table className="w-full text-xs">
        <tbody className="divide-y divide-gray-700">
          <tr><td>Metformin</td><td>Morning</td><td>Taken</td></tr>
          <tr><td>Insulin</td><td>Night</td><td>Missed</td></tr>
          <tr><td>Vitamin D</td><td>Weekly</td><td>Pending</td></tr>
        </tbody>
      </table>
    </Card>

    <Card title="Scan Medicine (QR)">
      <button className="bg-green-500 hover:bg-green-600 text-black font-medium w-full p-2.5 rounded transition">
        Simulate QR Scan
      </button>
    </Card>

    <Card title="Upload Prescription">
      <input
        type="file"
        className="w-full p-2 bg-black border border-gray-700 rounded text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white hover:file:bg-gray-600"
      />
    </Card>

    <PatientTrendChart />
  </div>
);

const FamilyTab = () => (
  <Card title="Family Health Monitoring">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-gray-700">
          <th className="text-left py-1">Member</th>
          <th className="text-left py-1">Disease</th>
          <th className="text-left py-1">Adherence</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700">
        <tr><td>Father</td><td>Diabetes</td><td>85%</td></tr>
        <tr><td>Mother</td><td>BP</td><td>92%</td></tr>
      </tbody>
    </table>
  </Card>
);

const DoctorTab = () => (
  <div className="space-y-4">
    <Card title="Doctor Patient List">
      <table className="w-full text-xs">
        <tbody className="divide-y divide-gray-700">
          <tr><td>Rahul</td><td>Diabetes</td><td>15 Mar</td></tr>
          <tr><td>Anita</td><td>Hypertension</td><td>17 Mar</td></tr>
        </tbody>
      </table>
    </Card>

    <DoctorRecoveryChart />

    <Card title="Teleconsult">
      <button className="bg-blue-500 hover:bg-blue-600 text-white font-medium w-full p-2.5 rounded transition">
        Start Video Consultation
      </button>
    </Card>
  </div>
);

const HospitalTab = () => (
  <Card title="Hospital Analytics">
    <table className="w-full text-xs">
      <tbody className="divide-y divide-gray-700">
        <tr><td>Total Patients</td><td>1240</td></tr>
        <tr><td>Admissions Today</td><td>37</td></tr>
        <tr><td>Bed Occupancy</td><td>82%</td></tr>
      </tbody>
    </table>
  </Card>
);

const HealthWorkerTab = () => (
  <Card title="Field Monitoring">
    <table className="w-full text-xs">
      <tbody className="divide-y divide-gray-700">
        <tr><td>Ward 12</td><td>33 Patients</td></tr>
        <tr><td>Ward 15</td><td>29 Patients</td></tr>
        <tr><td>Vaccinations Today</td><td>45</td></tr>
      </tbody>
    </table>
  </Card>
);

const PharmacyTab = () => (
  <Card title="Medicine Dispensing">
    <table className="w-full text-xs">
      <tbody className="divide-y divide-gray-700">
        <tr><td>Insulin</td><td>120 Stock</td></tr>
        <tr><td>Metformin</td><td>300 Stock</td></tr>
        <tr><td>Vitamin D</td><td>200 Stock</td></tr>
      </tbody>
    </table>
  </Card>
);

const PharmaTab = () => <PharmaEffectivenessChart />;

const GovernmentTab = () => <GovtDiseaseChart />;

const AdminTab = () => (
  <Card title="Platform Metrics">
    <table className="w-full text-xs">
      <tbody className="divide-y divide-gray-700">
        <tr><td>Total Patients</td><td>20000</td></tr>
        <tr><td>Doctors</td><td>600</td></tr>
        <tr><td>Hospitals</td><td>45</td></tr>
        <tr><td>Pharmacies</td><td>320</td></tr>
      </tbody>
    </table>
  </Card>
);

// ────────────────────────────────────────────────
// Main Component
const ThinkMedBoxHome = () => {
  const [activeTab, setActiveTab] = useState("patient");

  const tabs = [
    "patient", "family", "doctor", "hospital",
    "healthworker", "pharmacy", "pharma", "government", "admin",
  ];

  const TabButton = ({ name }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`p-2.5 text-xs font-medium rounded-lg capitalize transition
        ${activeTab === name
          ? "bg-green-500 text-black shadow-sm"
          : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}
    >
      {name}
    </button>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "patient":     return <PatientTab />;
      case "family":      return <FamilyTab />;
      case "doctor":      return <DoctorTab />;
      case "hospital":    return <HospitalTab />;
      case "healthworker": return <HealthWorkerTab />;
      case "pharmacy":    return <PharmacyTab />;
      case "pharma":      return <PharmaTab />;
      case "government":  return <GovernmentTab />;
      case "admin":       return <AdminTab />;
      default:            return null;
    }
  };

  return (
    <div className="bg-black text-white min-h-screen p-4">
      {/* Header */}
      <div className="text-center text-xl font-bold mb-5">
        ThinkMedBox – Home Medicine Intelligence Platform
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-9 gap-2 mb-6">
        {tabs.map((tab) => (
          <TabButton key={tab} name={tab} />
        ))}
      </div>

      {/* Active Tab Content */}
      <div>{renderTabContent()}</div>
    </div>
  );
};

export default ThinkMedBoxHome;